import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import type { WorkshopProject, NodePotential, ElectricalWire, WireColor } from '../../model/types';
import {
  CoilSymbol,
  NoContactSymbol,
  NcContactSymbol,
  PowerContacts3PSymbol,
  ThermalHeaters3PSymbol,
  MotorSchematicSymbol,
  LampSchematicSymbol,
  SchematicPin,
} from './SchematicSymbols';
import { ElectricalSolver } from '../../solver/ElectricalSolver';
import { workshopAudio } from '../../audio/workshopAudio';

export interface SchematicViewProps {
  project: WorkshopProject;
  solver?: ElectricalSolver;
  onChangeProject?: (updated: WorkshopProject) => void;
  onUpdateDeviceProp?: (deviceId: string, propKey: string, value: unknown) => void;
  activeWireColor?: WireColor;
  isSimulating?: boolean;
}

/**
 * Elektrik Kumanda Atölyesi — Elektrik Şeması Görünümü (IEC 60617 Standart Şema)
 * Sol sütun: 3 Fazlı Güç Devresi (F1 -> KM1 -> FR1 -> M1)
 * Sağ sütun: Kumanda ve Mühürleme Devresi (F2 -> 95-96 -> Stop -> Start // KM1:13-14 -> KM1 Bobin -> N)
 *
 * Aşama 4: Tam Çift Yönlü Senkronizasyon (DualViewSync)
 * - project.wires grafındaki gerçek bağlantılara göre hatlar dinamik çizilir / silinir.
 * - Şema klemenslerine tıklayarak yeni kablo çekilebilir, seçilen kablo klavyeden veya butonla silinebilir.
 * - Klemens voltajları üzerine gelindiğinde canlı multimetre probu olarak gösterilir.
 */
export const SchematicView: React.FC<SchematicViewProps> = ({
  project,
  solver: externalSolver,
  onChangeProject,
  onUpdateDeviceProp,
  activeWireColor = '#1f2937',
  isSimulating = true,
}) => {
  const solver = useMemo(() => externalSolver || new ElectricalSolver(), [externalSolver]);

  // Etkileşim Durumları
  const [activePinId, setActivePinId] = useState<string | null>(null);
  const [selectedWireId, setSelectedWireId] = useState<string | null>(null);

  // Elektriksel düğüm gerilimlerini çöz
  const snapshot = useMemo(() => {
    if (!isSimulating) {
      return {
        simTimeMs: 0,
        nodePotentials: {} as Record<string, NodePotential>,
        deviceStates: {},
        wireEnergized: {},
        hasShortCircuit: false,
      };
    }
    return solver.solve(project);
  }, [solver, project, isSimulating]);

  // Cihaz referansları
  const f1 = project.devices.find((d) => d.id === 'f1');
  const f2 = project.devices.find((d) => d.id === 'f2');
  const fr1 = project.devices.find((d) => d.id === 'fr1');
  const sb1 = project.devices.find((d) => d.id === 'sb1');
  const sb2 = project.devices.find((d) => d.id === 'sb2');

  const f1Closed = f1?.props['closed'] !== false && !f1?.props['tripped'];
  const f2Closed = f2?.props['closed'] !== false && !f2?.props['tripped'];
  const km1Energized = snapshot.deviceStates['km1']?.energized ?? false;
  const fr1Tripped = fr1?.props['tripped'] === true;
  const stopPressed = sb1?.props['pressed'] === true;
  const startPressed = sb2?.props['pressed'] === true;
  const motorRunning = snapshot.deviceStates['m1']?.motorRunning ?? false;
  const hl1Lit = snapshot.deviceStates['hl1']?.energized ?? false;
  const hl2Lit = snapshot.deviceStates['hl2']?.energized ?? false;

  // Kontaktör Çekme / Bırakma Ses Geçişleri
  const prevKm1Energized = useRef<boolean>(false);
  useEffect(() => {
    if (km1Energized && !prevKm1Energized.current) {
      workshopAudio.playContactorEnergize();
    } else if (!km1Energized && prevKm1Energized.current) {
      workshopAudio.playContactorRelease();
    }
    prevKm1Energized.current = km1Energized;
  }, [km1Energized]);

  const prevHasShortCircuit = useRef<boolean>(false);
  useEffect(() => {
    if (snapshot.hasShortCircuit && !prevHasShortCircuit.current) {
      workshopAudio.playShortCircuitAlarm();
    }
    prevHasShortCircuit.current = snapshot.hasShortCircuit;
  }, [snapshot.hasShortCircuit]);

  // Tel rengi yardımcısı
  const getLineColor = (potKey: string, fallback = '#94a3b8'): string => {
    const pot = snapshot.nodePotentials[potKey];
    if (pot === 'L1') return '#f59e0b';
    if (pot === 'L2') return '#ea580c';
    if (pot === 'L3') return '#cbd5e1';
    if (pot === 'N') return '#38bdf8';
    if (pot === 'PE') return '#84cc16';
    return fallback;
  };

  // İki terminal arasında doğrudan kablo bağlantısı arama
  const findWire = useCallback(
    (d1: string, t1: string, d2: string, t2: string): ElectricalWire | undefined => {
      return project.wires.find(
        (w) =>
          (w.from.deviceId === d1 && w.from.terminalId === t1 && w.to.deviceId === d2 && w.to.terminalId === t2) ||
          (w.from.deviceId === d2 && w.from.terminalId === t2 && w.to.deviceId === d1 && w.to.terminalId === t1)
      );
    },
    [project.wires]
  );

  // Klemens Gerilim Tooltip Bilgisi
  const getPinTitle = useCallback(
    (devId: string, termId: string) => {
      const pot = snapshot.nodePotentials[`${devId}:${termId}`];
      const volt =
        pot === 'L1'
          ? '230V~ AC (L1 Fazı, Canlı)'
          : pot === 'L2'
          ? '230V~ AC (L2 Fazı, Canlı)'
          : pot === 'L3'
          ? '230V~ AC (L3 Fazı, Canlı)'
          : pot === 'N'
          ? '0V~ AC (Nötr Dönüşü)'
          : pot === 'PE'
          ? '0V (Koruma Topraklaması)'
          : '0V (Açık Devre / Enerji Yok)';
      return `${devId.toUpperCase()}:${termId} — ${volt}`;
    },
    [snapshot.nodePotentials]
  );

  // Terminale Tıklama — İki terminal arasında kablo çekme
  const handlePinClick = useCallback(
    (targetKey: string) => {
      if (!activePinId) {
        setActivePinId(targetKey);
      } else if (activePinId === targetKey) {
        setActivePinId(null);
      } else {
        const [fromDev, fromTerm] = activePinId.split(':');
        const [toDev, toTerm] = targetKey.split(':');

        if (fromDev && fromTerm && toDev && toTerm) {
          const newWire: ElectricalWire = {
            id: `w_${Date.now()}`,
            from: { deviceId: fromDev, terminalId: fromTerm },
            to: { deviceId: toDev, terminalId: toTerm },
            color: activeWireColor,
            sectionMm2: activeWireColor === '#78350f' || activeWireColor === '#84cc16' ? 2.5 : 1.5,
          };
          workshopAudio.playButtonClick();
          onChangeProject?.({
            ...project,
            wires: [...project.wires, newWire],
          });
        }
        setActivePinId(null);
      }
    },
    [activePinId, activeWireColor, project, onChangeProject]
  );

  // Seçili Kabloyu Silme
  const handleDeleteWire = useCallback(() => {
    if (!selectedWireId) return;
    workshopAudio.playButtonClick();
    onChangeProject?.({
      ...project,
      wires: project.wires.filter((w) => w.id !== selectedWireId),
    });
    setSelectedWireId(null);
  }, [selectedWireId, project, onChangeProject]);

  // Dinamik Hat Çizici Yardımcı
  const renderLineSegment = (
    d1: string,
    t1: string,
    d2: string,
    t2: string,
    renderSvg: (stroke: string, strokeW: number, isSelected: boolean) => React.ReactNode,
    alternativePairs?: [string, string, string, string][]
  ) => {
    let wire = findWire(d1, t1, d2, t2);
    if (!wire && alternativePairs) {
      for (const [ad1, at1, ad2, at2] of alternativePairs) {
        wire = findWire(ad1, at1, ad2, at2);
        if (wire) break;
      }
    }

    const isConnected = !!wire;
    const isSelected = !!wire && selectedWireId === wire.id;
    const potKey = `${d1}:${t1}`;
    const baseColor = isConnected ? getLineColor(potKey) : '#334155';
    const stroke = isSelected ? '#38bdf8' : baseColor;
    const strokeW = isSelected ? 3.5 : isConnected ? 1.8 : 0.8;

    return (
      <g
        key={`${d1}:${t1}-${d2}:${t2}`}
        style={{ cursor: isConnected ? 'pointer' : 'default' }}
        onClick={(e) => {
          if (wire) {
            e.stopPropagation();
            setSelectedWireId(wire.id);
          }
        }}
        opacity={isConnected ? 1 : 0.3}
      >
        {renderSvg(stroke, strokeW, isSelected)}
      </g>
    );
  };

  // Cihaz klemensleri için tooltip haritaları
  const km1PinTitles: Record<string, string> = {
    A1: getPinTitle('km1', 'A1'),
    A2: getPinTitle('km1', 'A2'),
    '1': getPinTitle('km1', '1'),
    '2': getPinTitle('km1', '2'),
    '3': getPinTitle('km1', '3'),
    '4': getPinTitle('km1', '4'),
    '5': getPinTitle('km1', '5'),
    '6': getPinTitle('km1', '6'),
    '13': getPinTitle('km1', '13'),
    '14': getPinTitle('km1', '14'),
  };

  const fr1PinTitles: Record<string, string> = {
    '1': getPinTitle('fr1', '1'),
    '2': getPinTitle('fr1', '2'),
    '3': getPinTitle('fr1', '3'),
    '4': getPinTitle('fr1', '4'),
    '5': getPinTitle('fr1', '5'),
    '6': getPinTitle('fr1', '6'),
    '95': getPinTitle('fr1', '95'),
    '96': getPinTitle('fr1', '96'),
    '97': getPinTitle('fr1', '97'),
    '98': getPinTitle('fr1', '98'),
  };

  return (
    <div
      className="schematic-view-container"
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        background: '#090d16',
        padding: '16px',
        overflow: 'auto',
      }}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Delete' || e.key === 'Backspace') {
          handleDeleteWire();
        } else if (e.key === 'Escape') {
          setActivePinId(null);
          setSelectedWireId(null);
        }
      }}
    >
      {/* Üst Şematik Bilgi ve Kablo Eylem Çubuğu */}
      <div
        style={{
          width: '100%',
          maxWidth: 900,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: '#131a26',
          border: '1px solid #232d3f',
          padding: '8px 16px',
          borderRadius: 8,
          marginBottom: 12,
          fontSize: 13,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontWeight: 700, color: '#38bdf8' }}>ELEKTRİK ŞEMASI (IEC 60617)</span>
          {activePinId ? (
            <span style={{ color: '#fbbf24', fontSize: 12 }}>
              Kablo Çekiliyor: <strong>{activePinId}</strong> (Bağlanacak terminale tıklayın)
            </span>
          ) : (
            <span style={{ color: '#94a3b8', fontSize: 12 }}>
              Sol: 3~ Güç Devresi (400V) | Sağ: Kumanda Devresi (230V)
            </span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {selectedWireId && (
            <button
              type="button"
              onClick={handleDeleteWire}
              style={{
                background: '#dc2626',
                color: '#fff',
                border: 'none',
                padding: '4px 10px',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              Seçili Kabloyu Sil
            </button>
          )}

          {snapshot.hasShortCircuit && (
            <span style={{ color: '#ef4444', fontWeight: 'bold' }}>
              ⚠️ {snapshot.shortCircuitMessage || 'Kısa Devre!'}
            </span>
          )}

          {motorRunning ? (
            <span style={{ color: '#22c55e', fontWeight: 'bold' }}>● Motor Devrede (Mühürlü)</span>
          ) : (
            <span style={{ color: '#94a3b8' }}>○ Motor Duruyor</span>
          )}
        </div>
      </div>

      {/* Şema SVG Tuvali (Teknik Çizim Standart Izgarası) */}
      <div
        style={{
          position: 'relative',
          width: 900,
          height: 680,
          background: '#0f172a',
          border: '1.5px solid #334155',
          borderRadius: 12,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
        }}
        onClick={() => {
          setActivePinId(null);
          setSelectedWireId(null);
        }}
      >
        <svg width="900" height="680" viewBox="0 0 900 680" style={{ display: 'block' }}>
          <defs>
            <pattern id="schematicGrid" width="20" height="20" patternUnits="userSpaceOnUse">
              <circle cx="10" cy="10" r="0.6" fill="#334155" />
            </pattern>
          </defs>

          {/* Izgara Arka Planı */}
          <rect width="900" height="680" fill="url(#schematicGrid)" />

          {/* Sütun Ayırıcı Kesikli Çizgi */}
          <line x1="380" y1="20" x2="380" y2="660" stroke="#334155" strokeWidth="1.2" strokeDasharray="6,4" />

          {/* ============================================================ */}
          {/* SOL BÖLÜM — 3 FAZLI GÜÇ DEVRESİ (POWER CIRCUIT)               */}
          {/* ============================================================ */}
          <g id="power-circuit">
            <text x="50" y="40" fill="#38bdf8" fontSize="13" fontWeight="bold">
              GÜÇ DEVRESİ (3~ 400V)
            </text>

            {/* Üst Yatay Güç Baraları (L1, L2, L3, PE) */}
            <g style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); handlePinClick('pwr:L1'); }}>
              <text x="40" y="74" fill="#f59e0b" fontSize="9" fontWeight="bold">L1</text>
              <line x1="60" y1="70" x2="330" y2="70" stroke={getLineColor('pwr:L1', '#f59e0b')} strokeWidth="1.8" />
              <SchematicPin label="" cx={60} cy={70} isEnergized={true} isSelected={activePinId === 'pwr:L1'} title="PWR:L1 Besleme Fazı" />
            </g>

            <g style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); handlePinClick('pwr:L2'); }}>
              <text x="40" y="86" fill="#ea580c" fontSize="9" fontWeight="bold">L2</text>
              <line x1="60" y1="82" x2="330" y2="82" stroke={getLineColor('pwr:L2', '#ea580c')} strokeWidth="1.8" />
              <SchematicPin label="" cx={60} cy={82} isEnergized={true} isSelected={activePinId === 'pwr:L2'} title="PWR:L2 Besleme Fazı" />
            </g>

            <g style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); handlePinClick('pwr:L3'); }}>
              <text x="40" y="98" fill="#cbd5e1" fontSize="9" fontWeight="bold">L3</text>
              <line x1="60" y1="94" x2="330" y2="94" stroke={getLineColor('pwr:L3', '#cbd5e1')} strokeWidth="1.8" />
              <SchematicPin label="" cx={60} cy={94} isEnergized={true} isSelected={activePinId === 'pwr:L3'} title="PWR:L3 Besleme Fazı" />
            </g>

            <g style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); handlePinClick('pwr:PE'); }}>
              <text x="40" y="110" fill="#84cc16" fontSize="9" fontWeight="bold">PE</text>
              <line x1="60" y1="106" x2="330" y2="106" stroke="#84cc16" strokeWidth="1.4" strokeDasharray="6,3" />
              <SchematicPin label="" cx={60} cy={106} isEnergized={false} isSelected={activePinId === 'pwr:PE'} title="PWR:PE Topraklama Barası" />
            </g>

            {/* F1: 3 Kutuplu Güç Sigortası (X=140, Y=145) */}
            <g
              style={{ cursor: 'pointer' }}
              onClick={() => {
                workshopAudio.playBreakerToggle();
                onUpdateDeviceProp?.('f1', 'closed', !f1Closed);
              }}
            >
              <PowerContacts3PSymbol
                designator="F1 (16A)"
                isClosed={f1Closed}
                isEnergized={true}
                x={140}
                y={145}
                onPinClick={(pin) => handlePinClick(`f1:${pin}`)}
                activePinId={activePinId?.startsWith('f1:') ? activePinId.split(':')[1] : null}
                pinTitles={{
                  '1': getPinTitle('f1', '1'),
                  '2': getPinTitle('f1', '2'),
                  '3': getPinTitle('f1', '3'),
                  '4': getPinTitle('f1', '4'),
                  '5': getPinTitle('f1', '5'),
                  '6': getPinTitle('f1', '6'),
                }}
              />
            </g>

            {/* F1 Giriş Dikey Hatları (Dinamik Bağlantı) */}
            {renderLineSegment('pwr', 'L1', 'f1', '1', (stroke, w) => (
              <line x1="140" y1="70" x2="140" y2="125" stroke={stroke} strokeWidth={w} />
            ))}
            {renderLineSegment('pwr', 'L2', 'f1', '3', (stroke, w) => (
              <line x1="168" y1="82" x2="168" y2="125" stroke={stroke} strokeWidth={w} />
            ))}
            {renderLineSegment('pwr', 'L3', 'f1', '5', (stroke, w) => (
              <line x1="196" y1="94" x2="196" y2="125" stroke={stroke} strokeWidth={w} />
            ))}

            {/* KM1: 3 Kutuplu Güç Kontakları (X=140, Y=250) */}
            <PowerContacts3PSymbol
              designator="KM1"
              isClosed={km1Energized}
              isEnergized={f1Closed}
              x={140}
              y={250}
              onPinClick={(pin) => handlePinClick(`km1:${pin}`)}
              activePinId={activePinId?.startsWith('km1:') ? activePinId.split(':')[1] : null}
              pinTitles={km1PinTitles}
            />

            {/* F1 ile KM1 Arası Dikey Hatlar */}
            {renderLineSegment('f1', '2', 'km1', '1', (stroke, w) => (
              <line x1="140" y1="165" x2="140" y2="230" stroke={stroke} strokeWidth={w} />
            ))}
            {renderLineSegment('f1', '4', 'km1', '3', (stroke, w) => (
              <line x1="168" y1="165" x2="168" y2="230" stroke={stroke} strokeWidth={w} />
            ))}
            {renderLineSegment('f1', '6', 'km1', '5', (stroke, w) => (
              <line x1="196" y1="165" x2="196" y2="230" stroke={stroke} strokeWidth={w} />
            ))}

            {/* FR1: Termik Röle Bimetal Isıtıcıları (X=140, Y=355) */}
            <ThermalHeaters3PSymbol
              designator="FR1 (5A)"
              isEnergized={f1Closed && km1Energized}
              x={140}
              y={355}
              onPinClick={(pin) => handlePinClick(`fr1:${pin}`)}
              activePinId={activePinId?.startsWith('fr1:') ? activePinId.split(':')[1] : null}
              pinTitles={fr1PinTitles}
            />

            {/* KM1 ile FR1 Arası Dikey Hatlar */}
            {renderLineSegment('km1', '2', 'fr1', '1', (stroke, w) => (
              <line x1="140" y1="270" x2="140" y2="337" stroke={stroke} strokeWidth={w} />
            ))}
            {renderLineSegment('km1', '4', 'fr1', '3', (stroke, w) => (
              <line x1="168" y1="270" x2="168" y2="337" stroke={stroke} strokeWidth={w} />
            ))}
            {renderLineSegment('km1', '6', 'fr1', '5', (stroke, w) => (
              <line x1="196" y1="270" x2="196" y2="337" stroke={stroke} strokeWidth={w} />
            ))}

            {/* M1: 3 Fazlı Asenkron Motor Sembolü (X=140, Y=470) */}
            <MotorSchematicSymbol
              designator="M1"
              isRunning={motorRunning}
              x={140}
              y={470}
              onPinClick={(pin) => handlePinClick(`m1:${pin}`)}
              activePinId={activePinId?.startsWith('m1:') ? activePinId.split(':')[1] : null}
              pinTitles={{
                U1: getPinTitle('m1', 'U1'),
                V1: getPinTitle('m1', 'V1'),
                W1: getPinTitle('m1', 'W1'),
                PE: getPinTitle('m1', 'PE'),
              }}
            />

            {/* FR1 ile Motor Arası Dikey Hatlar */}
            {renderLineSegment('fr1', '2', 'm1', 'U1', (stroke, w) => (
              <line x1="154" y1="373" x2="154" y2="454" stroke={stroke} strokeWidth={w} />
            ))}
            {renderLineSegment('fr1', '4', 'm1', 'V1', (stroke, w) => (
              <line x1="168" y1="373" x2="168" y2="454" stroke={stroke} strokeWidth={w} />
            ))}
            {renderLineSegment('fr1', '6', 'm1', 'W1', (stroke, w) => (
              <line x1="182" y1="373" x2="182" y2="454" stroke={stroke} strokeWidth={w} />
            ))}

            {/* PE Toprak Hattının Motora İnişi */}
            {renderLineSegment('pwr', 'PE', 'm1', 'PE', (stroke, w) => (
              <path d="M 230 106 L 230 494 L 204 494" fill="none" stroke={stroke} strokeWidth={w} strokeDasharray="6,3" />
            ))}
          </g>

          {/* ============================================================ */}
          {/* SAĞ BÖLÜM — KUMANDA VE MÜHÜRLEME DEVRESİ (CONTROL CIRCUIT)   */}
          {/* ============================================================ */}
          <g id="control-circuit">
            <text x="440" y="40" fill="#38bdf8" fontSize="13" fontWeight="bold">
              KUMANDA DEVRESİ (230V~)
            </text>

            {/* Üst Yatay Faz Hattı (L1) */}
            <g style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); handlePinClick('pwr:L1'); }}>
              <text x="420" y="74" fill="#f59e0b" fontSize="9" fontWeight="bold">L1</text>
              <line x1="440" y1="70" x2="840" y2="70" stroke={getLineColor('pwr:L1', '#f59e0b')} strokeWidth="1.8" />
              <SchematicPin label="" cx={440} cy={70} isEnergized={true} isSelected={activePinId === 'pwr:L1'} title="PWR:L1 Kumanda Besleme Fazı" />
            </g>

            {/* Alt Yatay Nötr Hattı (N) */}
            <g style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); handlePinClick('pwr:N'); }}>
              <text x="420" y="624" fill="#38bdf8" fontSize="9" fontWeight="bold">N</text>
              <line x1="440" y1="620" x2="840" y2="620" stroke={getLineColor('pwr:N', '#38bdf8')} strokeWidth="1.8" />
              <SchematicPin label="" cx={440} cy={620} isEnergized={true} isSelected={activePinId === 'pwr:N'} title="PWR:N Şebeke Nötr Barası" />
            </g>

            {/* 1. Kumanda Kolu: F2 -> 95-96 -> Stop -> Start//13-14 -> Bobin -> N */}
            <g transform="translate(540, 0)">
              {/* L1'den F2'ye İniş */}
              {renderLineSegment('pwr', 'L1', 'f2', '1', (stroke, w) => (
                <line x1="0" y1="70" x2="0" y2="100" stroke={stroke} strokeWidth={w} />
              ))}

              {/* F2: 1 Kutuplu Kumanda Sigortası (Y=120) */}
              <g
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  workshopAudio.playBreakerToggle();
                  onUpdateDeviceProp?.('f2', 'closed', !f2Closed);
                }}
              >
                <NoContactSymbol
                  labelFrom="1"
                  labelTo="2"
                  designator="F2 (6A)"
                  isClosed={f2Closed}
                  isEnergized={true}
                  x={0}
                  y={120}
                  onPinClick={(pin) => handlePinClick(`f2:${pin}`)}
                  activePinId={activePinId?.startsWith('f2:') ? activePinId.split(':')[1] : null}
                  pinTitles={{
                    '1': getPinTitle('f2', '1'),
                    '2': getPinTitle('f2', '2'),
                  }}
                />
              </g>

              {/* F2'den FR1 (95-96)'ya Hat */}
              {renderLineSegment('f2', '2', 'fr1', '95', (stroke, w) => (
                <line x1="0" y1="140" x2="0" y2="175" stroke={stroke} strokeWidth={w} />
              ))}

              {/* FR1: Termik NC Koruma Kontağı (95-96, Y=195) */}
              <g
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  workshopAudio.playThermalTrip();
                  onUpdateDeviceProp?.('fr1', 'tripped', !fr1Tripped);
                }}
              >
                <NcContactSymbol
                  labelFrom="95"
                  labelTo="96"
                  designator="FR1 (NC)"
                  isOpen={fr1Tripped}
                  isEnergized={f2Closed}
                  x={0}
                  y={195}
                  onPinClick={(pin) => handlePinClick(`fr1:${pin}`)}
                  activePinId={activePinId?.startsWith('fr1:') ? activePinId.split(':')[1] : null}
                  pinTitles={fr1PinTitles}
                />
              </g>

              {/* FR1'den Stop Butonuna Hat */}
              {renderLineSegment('fr1', '96', 'sb1', '1', (stroke, w) => (
                <line x1="0" y1="215" x2="0" y2="250" stroke={stroke} strokeWidth={w} />
              ))}

              {/* SB1: Stop Butonu (1-2 NC, Y=270) */}
              <g
                style={{ cursor: 'pointer' }}
                onMouseDown={() => {
                  workshopAudio.playButtonClick();
                  onUpdateDeviceProp?.('sb1', 'pressed', true);
                }}
                onMouseUp={() => onUpdateDeviceProp?.('sb1', 'pressed', false)}
              >
                <NcContactSymbol
                  labelFrom="1"
                  labelTo="2"
                  designator="SB1 (STOP)"
                  isOpen={stopPressed}
                  isEnergized={f2Closed && !fr1Tripped}
                  x={0}
                  y={270}
                  onPinClick={(pin) => handlePinClick(`sb1:${pin}`)}
                  activePinId={activePinId?.startsWith('sb1:') ? activePinId.split(':')[1] : null}
                  pinTitles={{
                    '1': getPinTitle('sb1', '1'),
                    '2': getPinTitle('sb1', '2'),
                  }}
                />
              </g>

              {/* Stop Çıkışı Düğüm Noktası (Start girişine hat) */}
              {renderLineSegment('sb1', '2', 'sb2', '3', (stroke, w) => (
                <g>
                  <circle cx="0" cy="305" r="3.5" fill={stroke} />
                  <line x1="0" y1="290" x2="0" y2="330" stroke={stroke} strokeWidth={w} />
                </g>
              ))}

              {/* Mühürleme Giriş Köprüsü (Stop 2 -> KM1 13) */}
              {renderLineSegment(
                'sb1',
                '2',
                'km1',
                '13',
                (stroke, w) => (
                  <g>
                    <line x1="0" y1="305" x2="80" y2="305" stroke={stroke} strokeWidth={w} />
                    <line x1="80" y1="305" x2="80" y2="330" stroke={stroke} strokeWidth={w} />
                  </g>
                ),
                [['sb2', '3', 'km1', '13']]
              )}

              {/* SB2: Start Butonu (3-4 NO, Y=350) */}
              <g
                style={{ cursor: 'pointer' }}
                onMouseDown={() => {
                  workshopAudio.playButtonClick();
                  onUpdateDeviceProp?.('sb2', 'pressed', true);
                }}
                onMouseUp={() => onUpdateDeviceProp?.('sb2', 'pressed', false)}
              >
                <NoContactSymbol
                  labelFrom="3"
                  labelTo="4"
                  designator="SB2 (START)"
                  isClosed={startPressed}
                  isEnergized={f2Closed && !fr1Tripped && !stopPressed}
                  x={0}
                  y={350}
                  onPinClick={(pin) => handlePinClick(`sb2:${pin}`)}
                  activePinId={activePinId?.startsWith('sb2:') ? activePinId.split(':')[1] : null}
                  pinTitles={{
                    '3': getPinTitle('sb2', '3'),
                    '4': getPinTitle('sb2', '4'),
                  }}
                />
              </g>

              {/* KM1: Mühürleme Kontağı (13-14 NO, Y=350) */}
              <NoContactSymbol
                labelFrom="13"
                labelTo="14"
                designator="KM1 (13-14)"
                isClosed={km1Energized}
                isEnergized={f2Closed && !fr1Tripped && !stopPressed}
                x={80}
                y={350}
                onPinClick={(pin) => handlePinClick(`km1:${pin}`)}
                activePinId={activePinId?.startsWith('km1:') ? activePinId.split(':')[1] : null}
                pinTitles={km1PinTitles}
              />

              {/* Start Butonu Çıkışından KM1 Bobinine Hat */}
              {renderLineSegment('sb2', '4', 'km1', 'A1', (stroke, w) => (
                <line x1="0" y1="370" x2="0" y2="395" stroke={stroke} strokeWidth={w} />
              ))}

              {/* Mühürleme Çıkış Köprüsü (KM1 14 -> KM1 A1) */}
              {renderLineSegment(
                'km1',
                '14',
                'km1',
                'A1',
                (stroke, w) => (
                  <g>
                    <line x1="80" y1="370" x2="80" y2="395" stroke={stroke} strokeWidth={w} />
                    <line x1="0" y1="395" x2="80" y2="395" stroke={stroke} strokeWidth={w} />
                  </g>
                ),
                [['km1', '14', 'sb2', '4']]
              )}

              {/* KM1 Bobinine İniş ve Düğüm Noktası */}
              <circle cx="0" cy="395" r="3.5" fill={getLineColor('km1:A1')} />
              <line x1="0" y1="395" x2="0" y2="442" stroke={getLineColor('km1:A1')} strokeWidth="1.8" />

              {/* KM1: Kontaktör Bobini (Y=470) */}
              <CoilSymbol
                designator="KM1"
                isEnergized={km1Energized}
                x={0}
                y={470}
                onPinClick={(pin) => handlePinClick(`km1:${pin}`)}
                activePinId={activePinId?.startsWith('km1:') ? activePinId.split(':')[1] : null}
                pinTitles={km1PinTitles}
              />

              {/* KM1 Bobininden Nötr Hattına İniş */}
              {renderLineSegment('km1', 'A2', 'pwr', 'N', (stroke, w) => (
                <line x1="0" y1="498" x2="0" y2="620" stroke={stroke} strokeWidth={w} />
              ))}
            </g>

            {/* 2. Kumanda Kolu: HL1 (Yeşil Çalışıyor Lambası) */}
            <g transform="translate(680, 0)">
              {/* KM1:A1'den HL1:X1'e Paralel Bağlantı */}
              {renderLineSegment(
                'km1',
                'A1',
                'hl1',
                'X1',
                (stroke, w) => (
                  <g>
                    <line x1="-140" y1="410" x2="0" y2="410" stroke={stroke} strokeWidth={w} />
                    <line x1="0" y1="410" x2="0" y2="446" stroke={stroke} strokeWidth={w} />
                    <circle cx="-140" cy="410" r="3.5" fill={stroke} />
                  </g>
                ),
                [['sb2', '4', 'hl1', 'X1']]
              )}

              {/* HL1: Yeşil Çalışma Lambası (Y=470) */}
              <LampSchematicSymbol
                designator="HL1 (RUN)"
                color="#22c55e"
                isLit={hl1Lit}
                x={0}
                y={470}
                onPinClick={(pin) => handlePinClick(`hl1:${pin}`)}
                activePinId={activePinId?.startsWith('hl1:') ? activePinId.split(':')[1] : null}
                pinTitles={{
                  X1: getPinTitle('hl1', 'X1'),
                  X2: getPinTitle('hl1', 'X2'),
                }}
              />

              {/* HL1'den Nötr Hattına İniş */}
              {renderLineSegment(
                'hl1',
                'X2',
                'pwr',
                'N',
                (stroke, w) => (
                  <line x1="0" y1="494" x2="0" y2="620" stroke={stroke} strokeWidth={w} />
                ),
                [['hl1', 'X2', 'km1', 'A2']]
              )}
            </g>

            {/* 3. Kumanda Kolu: FR1 (97-98 NO) -> HL2 (Kırmızı Arıza Lambası) */}
            <g transform="translate(780, 0)">
              {/* F2 çıkışından Termik 97 klemensine hat */}
              {renderLineSegment(
                'f2',
                '2',
                'fr1',
                '97',
                (stroke, w) => (
                  <g>
                    <line x1="-240" y1="160" x2="0" y2="160" stroke={stroke} strokeWidth={w} />
                    <line x1="0" y1="160" x2="0" y2="240" stroke={stroke} strokeWidth={w} />
                    <circle cx="-240" cy="160" r="3.5" fill={stroke} />
                  </g>
                ),
                [['fr1', '95', 'fr1', '97']]
              )}

              {/* FR1: Termik NO Arıza Kontağı (97-98, Y=260) */}
              <NoContactSymbol
                labelFrom="97"
                labelTo="98"
                designator="FR1 (NO)"
                isClosed={fr1Tripped}
                isEnergized={f2Closed}
                x={0}
                y={260}
                onPinClick={(pin) => handlePinClick(`fr1:${pin}`)}
                activePinId={activePinId?.startsWith('fr1:') ? activePinId.split(':')[1] : null}
                pinTitles={fr1PinTitles}
              />

              {/* FR1 (98)'den HL2 (X1)'e Hat */}
              {renderLineSegment('fr1', '98', 'hl2', 'X1', (stroke, w) => (
                <line x1="0" y1="280" x2="0" y2="446" stroke={stroke} strokeWidth={w} />
              ))}

              {/* HL2: Kırmızı Arıza Lambası (Y=470) */}
              <LampSchematicSymbol
                designator="HL2 (TRIP)"
                color="#ef4444"
                isLit={hl2Lit}
                x={0}
                y={470}
                onPinClick={(pin) => handlePinClick(`hl2:${pin}`)}
                activePinId={activePinId?.startsWith('hl2:') ? activePinId.split(':')[1] : null}
                pinTitles={{
                  X1: getPinTitle('hl2', 'X1'),
                  X2: getPinTitle('hl2', 'X2'),
                }}
              />

              {/* HL2'den Nötr Hattına İniş */}
              {renderLineSegment(
                'hl2',
                'X2',
                'pwr',
                'N',
                (stroke, w) => (
                  <line x1="0" y1="494" x2="0" y2="620" stroke={stroke} strokeWidth={w} />
                ),
                [['hl2', 'X2', 'km1', 'A2'], ['hl2', 'X2', 'hl1', 'X2']]
              )}
            </g>
          </g>
        </svg>
      </div>
    </div>
  );
};
